import { BindAll } from 'lodash-decorators'
import { $service } from '@xt/client/experimental/request/service'
import moment from 'moment'
import $global from '@xt/client/store/global'
import singleServiceTime from '@xt/client/experimental/server-time'
import { action, observable, computed } from 'mobx'

import { AjaxBasics } from '../../helpers/ajaxBasics'
import { RandomWorks } from './random-works'
import { PublicCourse } from './public-course'
import { ArticleSharing } from './article-sharing'

import type { HomeJSONData, CourseButton, Course } from '@xt/client/types/home'

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[] ? ElementType : never

@BindAll()
export class ControllerV2Home {
  @observable dataSource: HomeJSONData | null = null

  RandomWorks: RandomWorks = new RandomWorks()

  PublicCourse: PublicCourse = new PublicCourse()

  ArticleSharing: ArticleSharing = new ArticleSharing()

  constructor($ajax: AjaxBasics) {}

  @computed
  get tonghaoAccessUrl() {
    if (!this.dataSource) return 'kindred-spirit'

    return singleServiceTime.serviceDate.isAfter(this.dataSource.guild.showTime) === true ? 'topic' : 'kindred-spirit'
  }

  /**
   * 客户端已经有的顶部banner渲染模版
   */
  private static AvailableBannerTemplates: Array<ArrayElement<HomeJSONData['banner']>['template']> = [
    'biz-about',
    'commodity',
    'commodity-show',
    'pure-image'
  ]

  async loadJSON() {
    if (!!this.dataSource) return

    const [err, data] = await $service<HomeJSONData>({
      method: 'GET',
      url: '/mocks/home-web-v2.json',
      baseURL: $global.static
    })

    if (err) throw err

    // 此处过滤一下需要展示的banner
    // 可能客户端没有对应的模版 此处应该过滤掉
    if (Array.isArray(data.banner)) {
      data.banner = data.banner.filter(config => {
        return ControllerV2Home.AvailableBannerTemplates.includes(config.template) && singleServiceTime.serviceDate.isAfter(config.showTime)
      })
    }

    this.updateDataSource(data)
  }

  // 传入课程项 返回对应的课程按钮配置
  mapCourseButton(courseItem: Course): null | CourseButton {
    const now = singleServiceTime.getServerTime('moment')

    const { startTime, endTime, signUpStartTime, signUpEndTime, label, isPermanent } = courseItem

    const startTimeMoment = moment(startTime)
    const endTimeMoment = moment(endTime)
    const signUpStartTimeMoment = moment(signUpStartTime)
    const signUpEndTimeMoment = moment(signUpEndTime)

    // JSON中配置了按钮文字 优先级最高 敬请期待 ｜ 课程未开始 在此配置
    if (typeof label === 'string' && label) {
      return {
        type: 'normal',
        label,
        className: 'style-gray'
      }
    }

    // 随报随学
    if (isPermanent === true) {
      return {
        type: 'normal',
        label: '随报随学',
        className: 'style-yellow'
      }
    }

    // 还没有到报名开始时间
    if (now.isBefore(signUpStartTimeMoment)) {
      return {
        type: 'normal',
        label: signUpStartTimeMoment.utcOffset(+8).format('MM月DD日报名'),
        className: 'style-yellow'
      }
    }

    // 报名已开始 且 报名还没有结束
    if (now.isAfter(signUpStartTimeMoment) && now.isBefore(signUpEndTimeMoment)) {
      const daysDiff = signUpEndTimeMoment.diff(now, 'days')
      const hoursDiff = signUpEndTimeMoment.diff(now, 'hours')
      if (hoursDiff > 24) {
        // case 1: 距报名结束大于24小时
        return {
          type: 'normal',
          label: `${daysDiff}天后报名结束`,
          className: 'style-red'
        }
      } else {
        // case 2: 距报名结束不足24小时
        return {
          type: 'countdown',
          suffix: '后报名结束',
          endTime: signUpEndTimeMoment.valueOf(),
          className: 'style-red'
        }
      }
    }

    // 全阶课 有转报时间，转报时间没有结束case
    if (courseItem.type === 'live-full') {
      const convertEndTimeMoment = moment(courseItem.convertEndTime)

      if (now.isBefore(convertEndTimeMoment)) {
        const daysDiff = convertEndTimeMoment.diff(now, 'days')
        const hoursDiff = convertEndTimeMoment.diff(now, 'hours')

        if (hoursDiff > 24) {
          // case 1: 距转报结束大于24小时
          return {
            type: 'normal',
            label: `${daysDiff}天后转报结束`,
            className: 'style-red'
          }
        } else {
          // case2: 距转报结束不足24小时
          return {
            type: 'countdown',
            suffix: '后转报结束',
            endTime: convertEndTimeMoment.valueOf(),
            className: 'style-red'
          }
        }
      }
    }

    // 报名已经结束了 但是还没有开课
    if (now.isAfter(signUpEndTimeMoment) && now.isBefore(startTimeMoment)) {
      return {
        type: 'normal',
        label: '报名已结束',
        className: 'style-gray'
      }
    }

    if (now.isAfter(startTimeMoment) && now.isBefore(endTimeMoment)) {
      return {
        type: 'normal',
        label: courseItem.type === 'show' ? '备展中' : '开课中',
        className: 'style-yellow'
      }
    }

    // 展览课程需要独立处理 投票时间
    if (courseItem.type === 'show') {
      const voteStartTimeMoment = moment(courseItem.voteStartTime)
      const voteEndTimeMoment = moment(courseItem.voteEndTime)

      if (now.isAfter(voteStartTimeMoment) && now.isBefore(voteEndTimeMoment)) {
        return {
          type: 'normal',
          label: '投票中',
          className: 'style-red'
        }
      } else if (now.isAfter(voteEndTimeMoment)) {
        return {
          type: 'normal',
          label: '观展',
          className: 'style-yellow'
        }
      }
    }

    if (now.isAfter(endTimeMoment)) {
      return {
        type: 'normal',
        label: courseItem.type === 'show' ? '备展结束' : '已结课',
        className: 'style-gray'
      }
    }

    return null
  }

  // 传入目标时间 格式化为倒计时格式字符串 HH时MM分ss秒
  formatEndTimeCountdownString(endTimestamp: number): string {
    const now = AjaxBasics.serviceDate

    if (now.isAfter(moment(endTimestamp))) {
      return `00秒`
    }

    const targetTime = moment(endTimestamp)
    const duration = moment.duration(targetTime.diff(now))

    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    const seconds = duration.seconds()

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}时`
    } else if (minutes > 0) {
      return `${minutes.toString().padStart(2, '0')}分`
    } else {
      // 倒计时已不足1分钟了
      return `${seconds.toString().padStart(2, '0')}秒`
    }
  }

  @action
  updateDataSource(data: HomeJSONData) {
    this.dataSource = data
  }
}
